/**
 * sentry.js — Sentry error & performance monitoring for the backoffice.
 *
 * Mirrors analytics.js: one shared client, environment derived from the
 * hostname, DSN overridable via VITE_SENTRY_DSN. Initialised on import (as
 * early as possible) so even startup errors are captured.
 *
 * The DSN is a public client identifier — safe to ship in the bundle, same as
 * the PostHog key. No-op on localhost so local development never sends events.
 */
import * as Sentry from '@sentry/browser';

// Public Sentry DSN — safe in the client bundle. Overridable via VITE_SENTRY_DSN.
const DSN = import.meta.env.VITE_SENTRY_DSN
  || 'https://97bdb9157471d33f3845e368b15c85b8@o4511613801725952.ingest.de.sentry.io/4511613820338256';

/**
 * Derive the deployment environment from the hostname — identical logic to
 * analytics.js so PostHog and Sentry agree on the environment label.
 */
function detectEnvironment() {
  const h = (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.endsWith('.local')) {
    return 'development';
  }
  if (h.endsWith('.pages.dev')) return 'staging';
  return 'production';
}

let initialised = false;

/** Initialise Sentry once. No-op on localhost so dev never pollutes the project. */
export function initSentry() {
  if (initialised) return;
  const environment = detectEnvironment();
  if (environment === 'development') {
    console.info('[sentry] development environment — Sentry disabled');
    return;
  }
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance tracing (Sentry project default).
    tracesSampleRate: 1.0,
    // Session Replay: 10% of normal sessions, 100% of sessions with an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  initialised = true;
}

/** Attach the logged-in user to subsequent events (mirrors analytics.identifyUser). */
export function setSentryUser(user) {
  if (!initialised || !user) return;
  Sentry.setUser({
    id: user.id != null ? String(user.id) : undefined,
    email: user.email,
    company_id: user.company_id,
    company_name: user.company_name,
  });
}

/** Clear the user context on logout (mirrors resetAnalytics). */
export function clearSentryUser() {
  if (!initialised) return;
  Sentry.setUser(null);
}

// Initialise on import so it runs before the rest of the app's modules load.
initSentry();
